// frontend/src/utils/fetchAllPages.js
//
// Read a DRF-paginated list to EXHAUSTION, or fail — the one home for
// "this picker needs every row".
//
// A page as large as the server's cap is not the complete list: it is a list
// cut short where the merchant cannot see the cut. `core/pagination.py` refuses
// an over-cap `page_size` with a 400, so this walks pages instead of asking for
// one big one.
//
// A list can also be too long to walk at all, and then the refusal below is
// the answer rather than a bigger page limit.

// A walk longer than this is not a reference list any more, whatever it is
// nominally a list OF. Stopping is the point: continuing would hold a picker
// open across hundreds of round trips, and the honest alternative to a very
// long walk is a server-side typeahead against the same endpoint, not a slower
// full load.
export const MAX_REFERENCE_PAGES = 40;

// How many rows one page of a reference walk asks for. Under `max_page_size`
// on every endpoint the SaaS walks (core/pagination.py: 1000), so the request
// is answerable rather than a 400 — and high enough that the realistic case is
// a single round trip.
export const REFERENCE_PAGE_SIZE = 500;

/**
 * Every row of a paginated endpoint, fetched through a Response-returning
 * client — `sessionApi` in the app, a stub with the same `get` in tests.
 *
 * @param {{get: (url: string) => Promise<Response>}} client
 * @param {string} path  Endpoint path, with or without an existing query string.
 * @param {object} [options]
 * @param {string} [options.label]  Names the list in the thrown message, so a
 *   report from a merchant identifies which picker gave up. Defaults to `path`.
 * @returns {Promise<Array>}
 */
export function fetchAllPagesVia(client, path, { label } = {}) {
  const join = path.includes('?') ? '&' : '?';
  return fetchAllPages(
    async (page) => {
      const res = await client.get(`${path}${join}page=${page}&page_size=${REFERENCE_PAGE_SIZE}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    { label: label ?? path },
  );
}

/**
 * Every row of a DRF-paginated list, across as many pages as it takes.
 *
 * Walks `page=1,2,3…` until the envelope's `next` is null. The walk is driven
 * by `next` rather than by `count` arithmetic so it stays correct when rows
 * are added between requests, and it is sequential rather than concurrent so a
 * merchant opening a picker cannot fan out dozens of simultaneous requests.
 *
 * Throws — it never returns a short list. A caller that cannot complete the
 * read has to surface that, because a partially-loaded picker is
 * indistinguishable from a genuinely short one: the merchant simply does not
 * find their supplier in it and picks the nearest thing that is there.
 *
 * @param {(page: number) => Promise<{next?: string|null, results?: Array}>} fetchPage
 *   Fetches ONE page, already parsed. Must reject on transport/HTTP failure.
 * @param {object} [options]
 * @param {string} [options.label] Names the list in the thrown message.
 * @returns {Promise<Array>} every row, in server order
 */
export async function fetchAllPages(fetchPage, { label = 'list' } = {}) {
  const rows = [];
  for (let page = 1; page <= MAX_REFERENCE_PAGES; page += 1) {
    const body = await fetchPage(page);
    const batch = body?.results;
    if (!Array.isArray(batch)) {
      // Not an empty page — a body that is not a DRF page at all. Reading it
      // as "no more rows" would end the walk early and return whatever came
      // before it as if it were complete.
      throw new Error(
        `${label}: page ${page} was not a DRF page envelope (no results array)`,
      );
    }
    rows.push(...batch);
    if (!body.next) return rows;
  }
  throw new Error(
    `${label}: still more rows after ${MAX_REFERENCE_PAGES} pages (${rows.length} loaded). ` +
    'This list is too long to load whole — it needs a search-as-you-type picker.',
  );
}
